import React, { useState, useEffect, useMemo } from "react";
import { dataService } from "../../lib/dataService.js";
import { C, font, btnS, btnP, btnD, card } from "../../lib/styles.js";
import FeedbackView from "./FeedbackView.jsx";
import { eqLabel } from "../../lib/equipment.js";
import notify from "../../lib/notify.jsx";

// --- Helpers ---

function formatDate(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function getMessageText(msg) {
  if (!msg) return "";
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content.filter((c) => c.type === "text").map((c) => c.text).join("\n");
  }
  return "";
}

function getMessageImages(msg) {
  if (!msg || !Array.isArray(msg.content)) return [];
  return msg.content.filter((c) => c.type === "image_ref" && c.url).map((c) => c.url);
}

function truncate(str, max) {
  if (!str || str.length <= max) return str || "";
  return str.slice(0, max).trimEnd() + "\u2026";
}

// Mirrors auto-fill strings written by nyc_website (PlannerConversation.jsx + api/submit-quote.js)
// when a visitor sends photos with no text. Keep in sync if those change.
const PLACEHOLDER_CAPTIONS = new Set([
  "Here are my reference photos.",
]);
const PLACEHOLDER_UPLOADED_PATTERN = /^\[Uploaded \d+ images?\]$/;

function isPlaceholderCaption(text) {
  const trimmed = text?.trim() || "";
  return !trimmed || PLACEHOLDER_CAPTIONS.has(trimmed) || PLACEHOLDER_UPLOADED_PATTERN.test(trimmed);
}

function getAttachmentGroups(messages) {
  if (!Array.isArray(messages)) return [];
  const groups = [];
  for (const msg of messages) {
    if (msg?.role !== "user") continue;
    const urls = getMessageImages(msg);
    if (urls.length === 0) continue;
    const text = getMessageText(msg);
    const snippet = isPlaceholderCaption(text) ? null : truncate(text, 240);
    groups.push({ snippet, urls });
  }
  return groups;
}

// --- Build compact "what needs scanning" description ---

const SIZE_SHORT = {
  jewelry: "jewelry", utensil: "small obj", shoe: "shoe-size", basketball: "mid obj",
  suitcase: "large obj", chair: "furniture", motorcycle: "vehicle-scale", car: "vehicle",
  boat: "oversized", building: "structure",
};

function buildWhat(doc, source) {
  if (source === "planner" || source === "quote_request") {
    const msgs = doc.conversationHistory || [];
    const userMsgs = msgs.filter((m) => m.role === "user");
    for (const msg of userMsgs) {
      const text = getMessageText(msg);
      if (text && !text.match(/^I want to capture.*Start the conversation/i)) {
        return truncate(text, 90);
      }
    }
    if (doc.report?.summary) return truncate(doc.report.summary, 90);
    return (doc.captureTypes || []).join(", ") || "\u2014";
  }

  // Assessment
  const data = doc.data || {};
  if (data._visionObservations) return truncate(data._visionObservations, 90);
  if (data.proj_notes) return truncate(data.proj_notes, 90);
  if (doc.aiSummary) return truncate(doc.aiSummary, 90);

  const ct = data.captureTypes || [];
  const parts = [];

  if (ct.includes("people")) {
    const qty = parseInt(data.ppl_qty) || 1;
    const areas = (data.ppl_areas || []).map((a) => a.replace("_", " ")).join(" + ");
    parts.push(`${qty} ${areas || "person"}`);
  }
  if (ct.includes("objects")) {
    const sq = data.obj_sq || {};
    const entries = Object.entries(sq).filter(([, v]) => parseInt(v) > 0);
    const total = entries.reduce((s, [, v]) => s + (parseInt(v) || 0), 0);
    if (total > 0) {
      const sizes = entries.map(([k, v]) => `${v} ${SIZE_SHORT[k] || k}`).join(", ");
      parts.push(sizes);
    } else {
      parts.push("objects");
    }
  }
  if (ct.includes("environments")) {
    const sqft = data.env_sqft;
    if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
    else parts.push("environment");
  }

  return parts.join(" + ") || "\u2014";
}

// --- Normalize sources into unified shape ---

function normalizePlannerSession(doc) {
  return {
    _docId: doc._docId,
    source: "planner",
    sourceLabel: "Plan",
    collection: "plannerSessions",
    createdAt: doc.createdAt || null,
    captureTypes: doc.captureTypes || [],
    contactEmail: doc.contactEmail || null,
    contactName: null,
    company: null,
    what: doc.subjectData?.description || buildWhat(doc, "planner"),
    reviewed: doc.reviewed || false,
    _raw: doc,
  };
}

function normalizeQuoteSubmission(doc) {
  return {
    _docId: doc._docId,
    source: "quote_request",
    sourceLabel: "Quote",
    collection: "quoteSubmissions",
    createdAt: doc.createdAt || null,
    captureTypes: doc.captureTypes || [],
    contactEmail: doc.contactEmail || null,
    contactName: doc.contactName || null,
    company: doc.company || null,
    what: doc.subjectData?.description || buildWhat(doc, "quote_request"),
    reviewed: doc.reviewed || false,
    _raw: doc,
  };
}

function normalizeAssessment(doc) {
  const data = doc.data || {};
  return {
    _docId: doc._docId,
    source: "assessment",
    sourceLabel: "Assessment",
    collection: "assessments",
    createdAt: doc.createdAt || null,
    captureTypes: doc.captureTypes || data.captureTypes || [],
    contactEmail: doc.contactEmail || data.c_email || data.ret_email || null,
    contactName: doc.contactName || data.c_name || null,
    company: data.c_company || null,
    what: buildWhat(doc, "assessment"),
    reviewed: doc.reviewed || false,
    _raw: doc,
  };
}

// --- Sort: unreviewed first, then date desc ---

function sortProspects(list) {
  const withTs = list.map((p) => ({ ...p, _ts: new Date(p.createdAt || 0).getTime() }));
  withTs.sort((a, b) => {
    if (!a.reviewed && b.reviewed) return -1;
    if (a.reviewed && !b.reviewed) return 1;
    return b._ts - a._ts;
  });
  return withTs;
}

// --- Main Component ---

export default function ProspectsView({ user }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "quote_request" | "planner"

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      dataService.plannerSessions.loadAll(),
      dataService.quoteSubmissions.loadAll(),
      dataService.assessments.loadAll(),
    ]).then(([plannerDocs, quoteDocs, assessmentDocs]) => {
      if (cancelled) return;

      const merged = [
        ...plannerDocs.map(normalizePlannerSession),
        ...quoteDocs.map(normalizeQuoteSubmission),
        ...assessmentDocs.map(normalizeAssessment),
      ];

      setProspects(merged);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  const handleMarkReviewed = async (prospect) => {
    const snapshot = prospects;
    setProspects((prev) =>
      prev.map((p) => p._docId === prospect._docId ? { ...p, reviewed: true } : p)
    );
    try {
      await dataService[prospect.collection].save({ id: prospect._docId, reviewed: true });
    } catch (e) {
      setProspects(snapshot);
      notify.error("Failed to mark as reviewed. Please try again.");
    }
  };

  const handleDelete = async (prospect) => {
    const snapshot = prospects;
    setProspects((prev) => prev.filter((p) => p._docId !== prospect._docId));
    setSelected(null);
    try {
      await dataService[prospect.collection].delete(prospect._docId);
    } catch (e) {
      setProspects(snapshot);
      notify.error("Failed to delete. Please try again.");
    }
  };

  const handleFeedbackSubmitted = async (prospect) => {
    const snapshot = prospects;
    setProspects((prev) =>
      prev.map((p) => p._docId === prospect._docId ? { ...p, reviewed: true } : p)
    );
    try {
      await dataService[prospect.collection].save({ id: prospect._docId, reviewed: true });
    } catch (e) {
      setProspects(snapshot);
      notify.error("Failed to save feedback status. Please try again.");
      return;
    }
    setFeedbackTarget(null);
    setSelected(null);
  };

  // Feedback view
  if (feedbackTarget) {
    return (
      <FeedbackView
        session={feedbackTarget._raw}
        user={user}
        onBack={() => setFeedbackTarget(null)}
        onSubmitted={() => handleFeedbackSubmitted(feedbackTarget)}
        parentCollection={feedbackTarget.collection}
      />
    );
  }

  // Detail view
  if (selected) {
    const detailProps = {
      prospect: selected,
      onBack: () => setSelected(null),
      onDelete: handleDelete,
      onMarkReviewed: handleMarkReviewed,
      onGiveFeedback: () => setFeedbackTarget(selected),
      onViewPlannerSession: (psId) => {
        const plannerProspect = prospects.find((p) => p._docId === psId);
        if (plannerProspect) setSelected(plannerProspect);
      },
    };

    if (selected.source === "planner") return <PlannerDetail {...detailProps} />;
    if (selected.source === "quote_request") return <QuoteSubmissionDetail {...detailProps} />;
    return <AssessmentDetail {...detailProps} />;
  }

  if (loading) return <Skeleton />;

  if (prospects.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={styles.emptyIcon}>&#9675;</div>
        <p>No prospects yet.</p>
        <p style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
          Prospects appear here after a visitor uses the planner or submits a quote request on newyorkcapture.com.
        </p>
      </div>
    );
  }

  const filtered = filter === "all" ? prospects : prospects.filter((p) => p.source === filter);
  const sorted = sortProspects(filtered);

  const counts = {
    all: prospects.length,
    quote_request: prospects.filter((p) => p.source === "quote_request").length,
    planner: prospects.filter((p) => p.source === "planner").length,
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={styles.filterBar}>
        {[
          { key: "all", label: "All" },
          { key: "quote_request", label: "Quote Requests" },
          { key: "planner", label: "Project Plans" },
        ].map((f) => (
          <button
            key={f.key}
            style={{
              ...styles.filterBtn,
              ...(filter === f.key ? styles.filterBtnActive : {}),
            }}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            <span style={styles.filterCount}>{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {/* Header */}
      <div style={styles.listHeader}>
        <span style={{ width: 24 }} />
        <span style={{ flex: 1 }}>Date</span>
        <span style={{ flex: 0.8 }}>Source</span>
        <span style={{ flex: 3 }}>What</span>
        <span style={{ flex: 1.5 }}>Contact</span>
        <span style={{ width: 32 }} />
      </div>

      {/* Rows */}
      {sorted.map((p) => (
        <ProspectRow key={p._docId} prospect={p} onClick={() => setSelected(p)} onDelete={() => handleDelete(p)} />
      ))}

      {/* Abandoned count placeholder */}
      {/* TODO: query abandoned sessions and show count here */}
    </div>
  );
}

// --- List Row ---

function ProspectRow({ prospect, onClick, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const p = prospect;

  if (confirmDelete) {
    return (
      <div style={{ ...styles.row, background: `${C.danger}08` }}>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, color: C.danger, fontWeight: 400 }}>Delete this prospect?</span>
          <button
            style={{ ...btnD, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            Yes, delete
          </button>
          <button
            style={{ ...btnS, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}
            onClick={(e) => { e.stopPropagation(); setConfirmDelete(false); }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ ...styles.row, background: hovered ? C.bg : C.surface }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Unreviewed dot */}
      <div style={{ width: 24, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {!p.reviewed && <div style={styles.unrevDot} />}
      </div>

      {/* Date */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: C.text }}>{formatDate(p.createdAt)}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{formatTime(p.createdAt)}</div>
      </div>

      {/* Source */}
      <div style={{ flex: 0.8 }}>
        <span style={p.source === "planner" ? styles.sourceBadgePlanner : p.source === "quote_request" ? styles.sourceBadgeQuote : styles.sourceBadgeAssessment}>
          {p.sourceLabel}
        </span>
      </div>

      {/* What */}
      <div style={{ flex: 3 }}>
        <div style={{ fontSize: 13, fontWeight: p.reviewed ? 300 : 400, color: C.text, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.what}
        </div>
        {p.captureTypes.length > 0 && (
          <div style={{ display: "flex", gap: 4, marginTop: 3 }}>
            {p.captureTypes.map((t) => <span key={t} style={styles.typeTag}>{t}</span>)}
          </div>
        )}
      </div>

      {/* Contact */}
      <div style={{ flex: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {p.contactName ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 400, color: C.text }}>{p.contactName}</div>
            {p.company && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{p.company}</div>}
          </>
        ) : p.contactEmail ? (
          <div style={{ fontSize: 12, fontWeight: 300, color: C.muted }}>{p.contactEmail}</div>
        ) : (
          <div style={{ fontSize: 12, fontWeight: 300, color: C.muted }}>{"\u2014"}</div>
        )}
      </div>

      {/* Delete */}
      <div
        style={{ width: 32, display: "flex", justifyContent: "center", alignItems: "center" }}
        onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
      >
        <span style={styles.deleteBtn}>&times;</span>
      </div>
    </div>
  );
}

// --- Planner Detail ---

function PlannerDetail({ prospect, onBack, onDelete, onMarkReviewed, onGiveFeedback }) {
  const raw = prospect._raw;
  const messages = raw.conversationHistory || [];
  const report = raw.report || {};
  const types = raw.captureTypes || [];
  const briefItems = raw.briefItems || [];

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <button style={styles.backBtn} onClick={onBack}>&larr; Back to prospects</button>

      {/* Header */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 400, color: C.text }}>
                {formatDate(raw.createdAt)} at {formatTime(raw.createdAt)}
              </div>
              <span style={styles.sourceBadgePlanner}>Planner</span>
              <ReviewedBadge reviewed={prospect.reviewed} />
            </div>
            {types.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {types.map((t) => <span key={t} style={styles.typeBadge}>{t}</span>)}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{raw._docId}</div>
        </div>

        {/* Brief items */}
        {briefItems.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={styles.sectionLabel}>Brief</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginTop: 8 }}>
              {briefItems.map((b, i) => (
                <div key={i}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{b.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{b.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report */}
      {report.summary && <ReportCard report={report} docId={raw._docId} reportType="planner" />}

      {/* Attachments */}
      <AttachmentsCard messages={messages} />

      {/* Conversation */}
      <ConversationCard messages={messages} />

      {/* Actions */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DeleteButton onConfirm={() => onDelete(prospect)} />
        <div style={{ display: "flex", gap: 8 }}>
          {!prospect.reviewed && (
            <button
              style={{ ...btnS, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
              onClick={() => onMarkReviewed(prospect)}
            >
              Mark Reviewed
            </button>
          )}
          <button
            style={{ ...btnP, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
            onClick={onGiveFeedback}
          >
            Give Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Quote Submission Detail ---

function QuoteSubmissionDetail({ prospect, onBack, onDelete, onMarkReviewed, onGiveFeedback, onViewPlannerSession }) {
  const raw = prospect._raw;
  const messages = raw.conversationHistory || [];
  const report = raw.report || {};
  const types = raw.captureTypes || [];
  const briefItems = raw.briefItems || [];

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <button style={styles.backBtn} onClick={onBack}>&larr; Back to prospects</button>

      {/* Header */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 400, color: C.text }}>
                {formatDate(raw.createdAt)} at {formatTime(raw.createdAt)}
              </div>
              <span style={styles.sourceBadgeQuote}>Quote Request</span>
              <ReviewedBadge reviewed={prospect.reviewed} />
            </div>
            {types.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {types.map((t) => <span key={t} style={styles.typeBadge}>{t}</span>)}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{raw._docId}</div>
        </div>

        {/* Contact info */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={styles.sectionLabel}>Contact</div>
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 24px" }}>
            {raw.contactName && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Name</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.contactName}</div>
              </div>
            )}
            {raw.contactEmail && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Email</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.contactEmail}</div>
              </div>
            )}
            {raw.contactPhone && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Phone</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.contactPhone}</div>
              </div>
            )}
            {raw.company && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Company</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.company}</div>
              </div>
            )}
            {raw.leadSource && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Lead Source</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.leadSource}</div>
              </div>
            )}
            {raw.industry && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Industry</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.industry}</div>
              </div>
            )}
            {raw.timeline && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Timeline</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.timeline}</div>
              </div>
            )}
            {raw.location && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Location</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.location}</div>
              </div>
            )}
            {raw.zipCode && (
              <div>
                <div style={{ fontSize: 11, color: C.muted }}>Zip Code</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.zipCode}</div>
              </div>
            )}
          </div>

          {/* Preferences */}
          {(raw.callMe || raw.mailingList || raw.plannerSessionId) && (
            <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {raw.callMe && (
                <span style={{ fontSize: 11, fontWeight: 400, color: C.amber, background: `${C.amber}12`, padding: "2px 8px", borderRadius: 4 }}>
                  Wants a call
                </span>
              )}
              {raw.mailingList && (
                <span style={{ fontSize: 11, fontWeight: 400, color: C.teal, background: `${C.teal}12`, padding: "2px 8px", borderRadius: 4 }}>
                  Mailing list
                </span>
              )}
              {raw.plannerSessionId && (
                <span
                  style={{ fontSize: 11, fontWeight: 400, color: C.purple, background: `${C.purple}12`, padding: "2px 8px", borderRadius: 4, cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => onViewPlannerSession(raw.plannerSessionId)}
                >
                  From planner &rsaquo;
                </span>
              )}
            </div>
          )}
        </div>

        {/* Brief items */}
        {briefItems.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={styles.sectionLabel}>Brief</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginTop: 8 }}>
              {briefItems.map((b, i) => (
                <div key={i}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{b.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{b.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Report */}
      {report.summary && <ReportCard report={report} docId={raw._docId} reportType="quote" />}

      {/* Attachments */}
      <AttachmentsCard messages={messages} />

      {/* Conversation */}
      <ConversationCard messages={messages} />

      {/* Actions */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DeleteButton onConfirm={() => onDelete(prospect)} />
        <div style={{ display: "flex", gap: 8 }}>
          {!prospect.reviewed && (
            <button
              style={{ ...btnS, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
              onClick={() => onMarkReviewed(prospect)}
            >
              Mark Reviewed
            </button>
          )}
          <button
            style={{ ...btnS, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
            onClick={onGiveFeedback}
          >
            Give Feedback
          </button>
          {/* Same mechanism as the inquiry-email button: reload with ?convert=
              and App.jsx routes into the pre-filled Quote Builder. */}
          <button
            style={{ ...btnP, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
            onClick={() => window.location.assign(`/?convert=${encodeURIComponent(raw._docId)}`)}
          >
            Convert to Quote
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Assessment Detail ---

function AssessmentDetail({ prospect, onBack, onDelete, onMarkReviewed, onGiveFeedback }) {
  const raw = prospect._raw;
  const data = raw.data || {};
  const types = raw.captureTypes || data.captureTypes || [];
  const hasConversation = (raw.conversationHistory || []).length > 0;

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      <button style={styles.backBtn} onClick={onBack}>&larr; Back to prospects</button>

      {/* Header */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 400, color: C.text }}>
                {formatDate(raw.createdAt)} at {formatTime(raw.createdAt)}
              </div>
              <span style={styles.sourceBadgeAssessment}>Assessment</span>
              <ReviewedBadge reviewed={prospect.reviewed} />
            </div>
            {types.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {types.map((t) => <span key={t} style={styles.typeBadge}>{t}</span>)}
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace" }}>{raw._docId}</div>
        </div>

        {/* Contact info */}
        {(raw.contactName || raw.contactEmail || data.c_name || data.c_email) && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={styles.sectionLabel}>Contact</div>
            <div style={{ marginTop: 8, display: "flex", gap: 24, flexWrap: "wrap" }}>
              {(raw.contactName || data.c_name) && (
                <div>
                  <div style={{ fontSize: 11, color: C.muted }}>Name</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.contactName || data.c_name}</div>
                </div>
              )}
              {(raw.contactEmail || data.c_email || data.ret_email) && (
                <div>
                  <div style={{ fontSize: 11, color: C.muted }}>Email</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{raw.contactEmail || data.c_email || data.ret_email}</div>
                </div>
              )}
              {data.c_company && (
                <div>
                  <div style={{ fontSize: 11, color: C.muted }}>Company</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{data.c_company}</div>
                </div>
              )}
              {data.industry && (
                <div>
                  <div style={{ fontSize: 11, color: C.muted }}>Industry</div>
                  <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{data.industry}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI Summary */}
      {raw.aiSummary && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={styles.sectionLabel}>AI Summary</div>
          <p style={{ fontSize: 13, fontWeight: 300, color: C.text, lineHeight: 1.5, marginTop: 8, maxWidth: "65ch", whiteSpace: "pre-wrap" }}>
            {raw.aiSummary}
          </p>
        </div>
      )}

      {/* Equipment Recommendations */}
      {Array.isArray(raw.equipment) && raw.equipment.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={styles.sectionLabel}>Equipment Recommendations</div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
            {raw.equipment.map((eq, i) => (
              <div key={i} style={{ fontSize: 13, fontWeight: 300, color: C.text }}>
                {eqLabel(eq)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {raw.timeline?.items?.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={styles.sectionLabel}>Timeline Estimates</div>
          <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            {raw.timeline.items.map((t, i) => (
              <div key={i}>
                <div style={{ fontSize: 11, color: C.muted }}>{t.label}</div>
                <div style={{ fontSize: 13, fontWeight: 400, color: C.text, marginTop: 2 }}>{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Assessment Notes */}
      {Array.isArray(raw.notes) && raw.notes.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={styles.sectionLabel}>Assessment Notes</div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {raw.notes.map((n, i) => (
              <div key={i} style={{ fontSize: 13, fontWeight: 300, color: C.text, lineHeight: 1.5 }}>
                {n}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Questionnaire */}
      <AssessmentDataCard data={data} />

      {/* Actions */}
      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DeleteButton onConfirm={() => onDelete(prospect)} />
        <div style={{ display: "flex", gap: 8 }}>
          {!prospect.reviewed && (
            <button
              style={{ ...btnS, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
              onClick={() => onMarkReviewed(prospect)}
            >
              Mark Reviewed
            </button>
          )}
          {hasConversation && (
            <button
              style={{ ...btnP, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }}
              onClick={onGiveFeedback}
            >
              Give Feedback
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Assessment questionnaire data card ---

function AssessmentDataCard({ data }) {
  if (!data || Object.keys(data).length === 0) return null;

  const ct = data.captureTypes || [];
  const sections = [];

  if (ct.includes("people")) {
    const fields = [];
    if (data.ppl_areas?.length) fields.push({ label: "Body areas", value: data.ppl_areas.join(", ") });
    if (data.ppl_qty) fields.push({ label: "Subjects", value: data.ppl_qty });
    if (data.ppl_poses?.length) fields.push({ label: "Poses", value: data.ppl_poses.join(", ") });
    if (data.ppl_loc) fields.push({ label: "Location", value: data.ppl_loc === "on_location" ? "On-location" : "Studio" });
    if (data.ppl_res) fields.push({ label: "Resolution", value: data.ppl_res });
    if (data.ppl_post) fields.push({ label: "Post-production", value: data.ppl_post });
    if (fields.length) sections.push({ title: "People", fields });
  }

  if (ct.includes("objects")) {
    const fields = [];
    if (data.obj_sizes?.length) fields.push({ label: "Sizes", value: data.obj_sizes.join(", ") });
    if (data.obj_sq) {
      const counts = Object.entries(data.obj_sq).filter(([, v]) => parseInt(v) > 0).map(([k, v]) => `${k}: ${v}`).join(", ");
      if (counts) fields.push({ label: "Quantities", value: counts });
    }
    if (data.obj_mats?.length) fields.push({ label: "Materials", value: data.obj_mats.join(", ") });
    if (data.obj_spray) fields.push({ label: "Spray OK", value: data.obj_spray });
    if (data.obj_complexity) fields.push({ label: "Geometry", value: data.obj_complexity });
    if (data.obj_color) fields.push({ label: "Color/texture", value: data.obj_color });
    if (data.obj_res) fields.push({ label: "Resolution", value: data.obj_res });
    if (data.obj_loc) fields.push({ label: "Location", value: data.obj_loc === "on_location" ? "On-location" : data.obj_loc === "ship" ? "Ship to studio" : "Studio" });
    if (data.obj_post) fields.push({ label: "Post-production", value: data.obj_post });
    if (fields.length) sections.push({ title: "Objects", fields });
  }

  if (ct.includes("environments")) {
    const fields = [];
    if (data.env_rooms) fields.push({ label: "Rooms", value: data.env_rooms });
    if (data.env_sqft) fields.push({ label: "Square footage", value: Number(data.env_sqft).toLocaleString() + " sqft" });
    if (data.env_level?.length) fields.push({ label: "Capture level", value: data.env_level.join(", ") });
    if (data.env_use?.length) fields.push({ label: "Use case", value: data.env_use.join(", ") });
    if (data.env_post) fields.push({ label: "Post-production", value: data.env_post });
    if (fields.length) sections.push({ title: "Environments", fields });
  }

  const general = [];
  if (data.timeline) general.push({ label: "Client timeline", value: data.timeline });
  if (data.experience) general.push({ label: "3D experience", value: data.experience === "yes" ? "Experienced" : data.experience === "some" ? "Some" : "First time" });
  if (data.returning) general.push({ label: "Returning client", value: data.returning === "yes" ? "Yes" : "No" });
  if (data.nda === "yes") general.push({ label: "NDA", value: "Requested" });
  if (data.proj_notes) general.push({ label: "Project notes", value: data.proj_notes });
  if (general.length) sections.push({ title: "General", fields: general });

  if (sections.length === 0) return null;

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={styles.sectionLabel}>Questionnaire Responses</div>
      {sections.map((sec, si) => (
        <div key={si} style={{ marginTop: 16, ...(si > 0 ? { paddingTop: 16, borderTop: `1px solid ${C.border}` } : {}) }}>
          <div style={{ fontSize: 12, fontWeight: 400, color: C.text, marginBottom: 8 }}>{sec.title}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 24px" }}>
            {sec.fields.map((f, fi) => (
              <div key={fi}>
                <div style={{ fontSize: 11, color: C.muted }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, marginTop: 2 }}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Reviewed Badge ---

function ReviewedBadge({ reviewed }) {
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      background: reviewed ? `${C.green}12` : `${C.blue}12`,
      color: reviewed ? C.green : C.blue,
    }}>
      {reviewed ? "Reviewed" : "Unreviewed"}
    </span>
  );
}

// --- Delete Button ---

function DeleteButton({ onConfirm }) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12, color: C.danger, fontWeight: 400 }}>Delete this prospect?</span>
        <button
          style={{ ...btnD, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}
          onClick={onConfirm}
        >
          Yes, delete
        </button>
        <button
          style={{ ...btnS, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}
          onClick={() => setConfirming(false)}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      style={{ ...btnD, padding: "6px 12px", fontSize: 11, cursor: "pointer" }}
      onClick={() => setConfirming(true)}
    >
      Delete
    </button>
  );
}

// --- Report Card ---

function ReportCard({ report, docId, reportType }) {
  const reportUrl = reportType === "quote"
    ? `https://newyorkcapture.com/quote/${docId}`
    : `https://newyorkcapture.com/planner/${docId}`;

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={styles.sectionLabel}>AI Report</div>
        <a
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, fontWeight: 400, color: C.blue, textDecoration: "none" }}
        >
          View report page &rsaquo;
        </a>
      </div>

      <p style={{ fontSize: 13, fontWeight: 300, color: C.text, lineHeight: 1.5, marginTop: 8, maxWidth: "65ch" }}>
        {report.summary}
      </p>

      {/* Equipment */}
      {Array.isArray(report.equipment) && report.equipment.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={styles.sectionLabel}>Equipment</div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {report.equipment.map((eq, i) => (
              <div key={i} style={{ fontSize: 13, fontWeight: 300, color: C.text }}>
                <span style={{ fontWeight: 400 }}>{eqLabel(eq)}</span>
                {(eq.role || eq.description || eq.reasoning) && (
                  <span style={{ color: C.muted }}> &mdash; {eq.role || eq.description || eq.reasoning}</span>
                )}
                {eq.type && <span style={{ color: C.muted, fontSize: 11 }}> ({eq.type})</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approach */}
      {Array.isArray(report.approach) && report.approach.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={styles.sectionLabel}>Capture Approach</div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
            {report.approach.map((step, i) => {
              const title = typeof step === "string" ? step : (step.title || step.step || step.name || "");
              const detail = typeof step === "string" ? "" : (step.detail || step.description || "");
              return (
                <div key={i}>
                  <div style={{ fontSize: 13, fontWeight: 400, color: C.text }}>
                    {i + 1}. {title}
                  </div>
                  {detail && (
                    <div style={{ fontSize: 13, fontWeight: 300, color: C.muted, marginTop: 2, lineHeight: 1.5, maxWidth: "65ch" }}>
                      {detail}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {report.notes && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <div style={styles.sectionLabel}>Additional Notes</div>
          <p style={{ fontSize: 13, fontWeight: 300, color: C.text, lineHeight: 1.5, marginTop: 8, maxWidth: "65ch", whiteSpace: "pre-wrap" }}>
            {report.notes}
          </p>
        </div>
      )}
    </div>
  );
}

// --- Attachments Card ---

function AttachmentsCard({ messages }) {
  const groups = useMemo(() => getAttachmentGroups(messages), [messages]);
  if (groups.length === 0) return null;

  const totalCount = groups.reduce((sum, g) => sum + g.urls.length, 0);

  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={styles.attHeader}>
        <div style={styles.sectionLabel}>Attachments</div>
        <div style={styles.attCount}>
          {totalCount} {totalCount === 1 ? "photo" : "photos"}
        </div>
      </div>

      {groups.map((group, gi) => (
        <div key={gi} style={{ marginBottom: gi < groups.length - 1 ? 18 : 0 }}>
          {group.snippet ? (
            <div style={styles.attSnippet}>{`\u201C${group.snippet}\u201D`}</div>
          ) : (
            <div style={styles.attNoCaption}>No caption</div>
          )}
          <div style={styles.attGrid}>
            {group.urls.map((url, ui) => (
              <a
                key={ui}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.attItem}
                title="Open full size in new tab"
              >
                <img
                  src={url}
                  alt={`Attachment ${ui + 1}`}
                  loading="lazy"
                  onError={(e) => {
                    const wrapper = e.currentTarget.parentElement;
                    if (wrapper) wrapper.style.display = "none";
                  }}
                  style={styles.attImg}
                />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Conversation Card ---

function ConversationCard({ messages }) {
  if (!messages || messages.length === 0) return null;

  return (
    <div style={{ ...card, padding: 0 }}>
      <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${C.border}` }}>
        <div style={styles.sectionLabel}>Conversation ({messages.length} messages)</div>
      </div>
      <div style={{ maxHeight: 600, overflowY: "auto" }}>
        {messages.map((msg, i) => {
          const isUser = msg.role === "user";
          const text = getMessageText(msg);
          const images = getMessageImages(msg);
          if (!text && images.length === 0) return null;
          return (
            <div
              key={i}
              style={{
                padding: "12px 16px",
                borderBottom: i < messages.length - 1 ? `1px solid ${C.border}` : "none",
                background: isUser ? C.surface : "#FAFAF8",
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.1em", color: isUser ? C.blue : C.teal, marginBottom: 6 }}>
                {isUser ? "Visitor" : "Prez"}
              </div>
              {images.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: text ? 8 : 0 }}>
                  {images.map((url, j) => (
                    <img key={j} src={url} alt={`Upload ${j + 1}`} onError={(e) => { e.target.style.display = "none"; }} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: `1px solid ${C.border}` }} />
                  ))}
                </div>
              )}
              {text && (
                <div style={{ fontSize: 13, fontWeight: 300, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap", maxWidth: "65ch" }}>
                  {text}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Skeleton ---

function Skeleton() {
  return (
    <div>
      <div style={styles.listHeader}>
        <span style={{ width: 24 }} />
        <span style={{ flex: 1 }}>Date</span>
        <span style={{ flex: 0.8 }}>Source</span>
        <span style={{ flex: 3 }}>What</span>
        <span style={{ flex: 1.5 }}>Contact</span>
        <span style={{ width: 32 }} />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ ...styles.row, pointerEvents: "none" }}>
          <div style={{ width: 24 }} />
          <div style={{ flex: 1 }}><div style={styles.skeletonBar} /></div>
          <div style={{ flex: 0.8 }}><div style={{ ...styles.skeletonBar, width: 50 }} /></div>
          <div style={{ flex: 3 }}><div style={{ ...styles.skeletonBar, width: "60%" }} /></div>
          <div style={{ flex: 1.5 }}><div style={{ ...styles.skeletonBar, width: 80 }} /></div>
          <div style={{ width: 32 }} />
        </div>
      ))}
    </div>
  );
}

// --- Styles ---

const styles = {
  filterBar: {
    display: "flex",
    gap: 4,
    marginBottom: 12,
  },
  filterBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    background: "transparent",
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 300,
    color: C.muted,
    cursor: "pointer",
    fontFamily: font,
    transition: "all 0.1s ease",
  },
  filterBtnActive: {
    background: C.text,
    borderColor: C.text,
    color: "#FFF",
    fontWeight: 400,
  },
  filterCount: {
    fontSize: 10,
    opacity: 0.7,
    fontVariantNumeric: "tabular-nums",
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    padding: "8px 16px",
    background: "#FAFAF8",
    borderRadius: "8px 8px 0 0",
    border: `1px solid ${C.border}`,
    borderBottom: "none",
    fontSize: 9.5,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
    fontFamily: font,
  },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    background: C.surface,
    borderLeft: `1px solid ${C.border}`,
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    cursor: "pointer",
    transition: "background 0.1s ease",
    fontFamily: font,
  },
  unrevDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: C.blue,
  },
  typeBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: `${C.teal}12`,
    color: C.teal,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  typeTag: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 500,
    background: `${C.teal}12`,
    color: C.teal,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  sourceBadgePlanner: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: `${C.purple}12`,
    color: C.purple,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  sourceBadgeQuote: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: `${C.amber}12`,
    color: C.amber,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  sourceBadgeAssessment: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: `${C.blue}12`,
    color: C.blue,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  empty: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: 32,
    textAlign: "center",
    color: C.muted,
    fontSize: 13,
    fontWeight: 300,
    fontFamily: font,
  },
  emptyIcon: {
    fontSize: 24,
    color: C.border,
    marginBottom: 8,
  },
  backBtn: {
    ...btnS,
    marginBottom: 16,
    cursor: "pointer",
    fontFamily: font,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
  },
  deleteBtn: {
    fontSize: 16,
    color: C.muted,
    cursor: "pointer",
    opacity: 0.4,
    transition: "all 0.1s ease",
    lineHeight: 1,
  },
  skeletonBar: {
    height: 12,
    borderRadius: 4,
    background: C.surfaceAlt,
    animation: "pulse 1.5s ease infinite",
    width: "60%",
  },
  attHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  attCount: {
    fontSize: 11,
    color: C.muted,
    fontVariantNumeric: "tabular-nums",
  },
  attSnippet: {
    fontSize: 12.5,
    fontWeight: 300,
    color: C.text,
    lineHeight: 1.5,
    fontStyle: "italic",
    marginBottom: 8,
    paddingLeft: 10,
    borderLeft: `2px solid ${C.border}`,
    maxWidth: "70ch",
  },
  attNoCaption: {
    fontSize: 12.5,
    fontWeight: 300,
    color: C.muted,
    fontStyle: "italic",
    marginBottom: 8,
    paddingLeft: 10,
    borderLeft: `2px solid ${C.border}`,
  },
  attGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
  },
  attItem: {
    display: "block",
    borderRadius: 6,
    overflow: "hidden",
    border: `1px solid ${C.border}`,
    background: C.surfaceAlt,
    textDecoration: "none",
    cursor: "pointer",
  },
  attImg: {
    width: "100%",
    height: 240,
    objectFit: "cover",
    display: "block",
  },
};
